'use client'

import { useEffect, useRef, useCallback } from 'react'

/**
 * Section metadata for tracking
 */
export interface SectionMeta {
  id: string
  label: string
  order: number
  type?: string
}

/**
 * Tracking configuration
 */
export interface UseTrackingOptions {
  /** Presentation ID for analytics */
  presentationId: string
  /** Access link ID (if using access control) */
  accessLinkId?: string
  /** Section metadata */
  sections: SectionMeta[]
  /** API endpoint for sending events */
  apiEndpoint?: string
  /** Whether tracking is enabled */
  enabled?: boolean
  /** Viewer email for identity tracking */
  viewerEmail?: string
}

/**
 * Analytics event types
 */
type EventType =
  | 'session_start'
  | 'section_enter'
  | 'section_exit'
  | 'section_visible'
  | 'session_end'
  | 'engagement'

/**
 * React hook for presentation tracking
 *
 * Initializes the Brixon tracking SDK and provides methods for
 * custom event tracking.
 *
 * @example
 * ```tsx
 * const { trackEngagement, sessionId } = useTracking({
 *   presentationId: 'acme-pitch',
 *   sections: [
 *     { id: 'hero', label: 'Introduction', order: 1 },
 *     { id: 'features', label: 'Features', order: 2 },
 *   ],
 * })
 *
 * // Track custom engagement
 * trackEngagement('cta_click', { buttonId: 'contact' })
 * ```
 */
export function useTracking(options: UseTrackingOptions) {
  const {
    presentationId,
    accessLinkId,
    sections,
    apiEndpoint = '/api/analytics/track',
    enabled = true,
    viewerEmail,
  } = options

  const sessionIdRef = useRef<string>(
    typeof crypto !== 'undefined' ? crypto.randomUUID() : `session-${Date.now()}`
  )
  const sessionStartRef = useRef<number>(Date.now())
  const currentSectionRef = useRef<string | null>(null)
  const sectionTimersRef = useRef<Map<string, number>>(new Map())
  const eventQueueRef = useRef<Array<Record<string, unknown>>>([])
  const observerRef = useRef<IntersectionObserver | null>(null)

  // Send events to API
  const flushEvents = useCallback(async () => {
    if (eventQueueRef.current.length === 0) return

    const events = [...eventQueueRef.current]
    eventQueueRef.current = []

    try {
      await fetch(apiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events }),
        keepalive: true,
      })
    } catch {
      // Re-queue on failure
      eventQueueRef.current = [...events, ...eventQueueRef.current]
    }
  }, [apiEndpoint])

  // Track event
  const trackEvent = useCallback(
    (type: EventType, data?: Record<string, unknown>) => {
      const event = {
        id: crypto.randomUUID(),
        access_link_id: accessLinkId,
        session_id: sessionIdRef.current,
        event_type: type,
        presentation_id: presentationId,
        viewer_email: viewerEmail,
        created_at: new Date().toISOString(),
        time_on_page: Math.round((Date.now() - sessionStartRef.current) / 1000),
        ...data,
      }

      eventQueueRef.current.push(event)

      // Batch flush
      if (eventQueueRef.current.length >= 10) {
        flushEvents()
      }
    },
    [accessLinkId, presentationId, viewerEmail, flushEvents]
  )

  // Section enter handler
  const onSectionEnter = useCallback(
    (sectionId: string) => {
      if (currentSectionRef.current === sectionId) return

      // Exit previous section
      if (currentSectionRef.current) {
        const enterTime = sectionTimersRef.current.get(currentSectionRef.current)
        const duration = enterTime ? Math.round((Date.now() - enterTime) / 1000) : 0
        const section = sections.find((s) => s.id === currentSectionRef.current)

        trackEvent('section_exit', {
          section_id: currentSectionRef.current,
          section_label: section?.label,
          section_order: section?.order,
          duration_seconds: duration,
        })

        sectionTimersRef.current.delete(currentSectionRef.current)
      }

      // Enter new section
      currentSectionRef.current = sectionId
      sectionTimersRef.current.set(sectionId, Date.now())

      const section = sections.find((s) => s.id === sectionId)
      trackEvent('section_enter', {
        section_id: sectionId,
        section_label: section?.label,
        section_order: section?.order,
      })
    },
    [sections, trackEvent]
  )

  // Setup tracking
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return

    // Track session start
    trackEvent('session_start')

    // Setup intersection observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const sectionId = entry.target.getAttribute('data-section-id')
          if (sectionId && entry.isIntersecting && entry.intersectionRatio > 0.5) {
            onSectionEnter(sectionId)
          }
        })
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] }
    )

    // Observe sections
    const sectionElements = document.querySelectorAll('[data-section-id]')
    sectionElements.forEach((el) => observerRef.current?.observe(el))

    // Batch flush interval
    const flushInterval = setInterval(flushEvents, 2000)

    // Session end handlers
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        trackEvent('session_end', {
          duration_seconds: Math.round((Date.now() - sessionStartRef.current) / 1000),
        })
        flushEvents()
      }
    }

    const handleBeforeUnload = () => {
      trackEvent('session_end', {
        duration_seconds: Math.round((Date.now() - sessionStartRef.current) / 1000),
      })
      flushEvents()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      clearInterval(flushInterval)
      observerRef.current?.disconnect()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [enabled, trackEvent, onSectionEnter, flushEvents])

  // Manual engagement tracking
  const trackEngagement = useCallback(
    (interactionType: string, metadata?: Record<string, unknown>) => {
      trackEvent('engagement', {
        metadata: {
          interaction_type: interactionType,
          ...metadata,
        },
      })
    },
    [trackEvent]
  )

  return {
    trackEngagement,
    sessionId: sessionIdRef.current,
  }
}
